/**
 * Radar „Ansprüche in Gefahr“ (tri-server `GET /:customer/claims/at-risk`, docs/claims.md): Mängel,
 * deren Gewährleistungsanspruch in den nächsten n Tagen verjährt — ohne Mängelanzeige oder
 * Verhandlung, die das Ende verschiebt. Ein Klick öffnet den Mangel.
 *
 * `onUnavailable` meldet, wenn der Server-Schalter aus ist (404): Dann zeigt das Panel den Reiter nicht.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ShieldAlert } from "lucide-react";

import type { ColumnDef } from "@tanstack/react-table";

import { useClaimsAPI, type AtRiskClaim, type ClaimsAtRisk } from "../../ClaimsAPI";
import { formatLegalDate, urgencyOfDays } from "./claimModel";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/ui/data-table";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const RADAR_HORIZONS = [30, 90, 180, 365] as const;

interface Props {
  customer: string;
  days: number;
  onDaysChange: (days: number) => void;
  onOpenDefect: (defectId: string) => void;
}

export default function ClaimsRadar({ customer, days, onDaysChange, onOpenDefect }: Props) {
  const { t } = useTranslation();
  const claimsAPI = useClaimsAPI(customer);
  const [result, setResult] = useState<ClaimsAtRisk | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    claimsAPI
      .listClaimsAtRisk(days)
      .then((loaded) => {
        if (cancelled) return;
        setUnavailable(loaded == null);
        setResult(loaded);
      })
      .catch((loadError: any) => !cancelled && setError(loadError?.message ?? String(loadError)))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [customer, days]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo<ColumnDef<AtRiskClaim>[]>(
    () => [
      {
        accessorKey: "mark",
        header: t("defectsPanel.columns.mark"),
        cell: ({ row }) => <span className="whitespace-nowrap font-mono text-xs">{row.original.mark ?? "—"}</span>,
      },
      {
        accessorKey: "title",
        header: t("defectsPanel.columns.title"),
        cell: ({ row }) => <span className="font-medium">{row.original.title ?? "—"}</span>,
      },
      {
        accessorKey: "assetName",
        header: t("defectsPanel.columns.asset"),
        cell: ({ row }) => row.original.assetName ?? row.original.asset ?? "—",
      },
      {
        accessorKey: "contractName",
        header: t("claims.radar.columns.contract"),
        cell: ({ row }) => row.original.contractName ?? "—",
      },
      {
        accessorKey: "limitationEnd",
        header: t("claims.radar.columns.end"),
        cell: ({ row }) => <span className="whitespace-nowrap">{formatLegalDate(row.original.limitationEnd)}</span>,
      },
      {
        accessorKey: "remainingDays",
        header: t("claims.radar.columns.remaining"),
        cell: ({ row }) => {
          const urgency = urgencyOfDays(row.original.remainingDays);

          return (
            <Badge variant={urgency === "critical" ? "destructive" : urgency === "soon" ? "warning" : "secondary"}>
              {t("claims.warranty.remaining", { count: row.original.remainingDays })}
              {row.original.uncertain ? ` · ${t("claims.radar.uncertain")}` : ""}
            </Badge>
          );
        },
      },
      {
        id: "notes",
        header: t("claims.radar.columns.notes"),
        cell: ({ row }) => <span className="text-xs text-muted-foreground">{row.original.notes.join("; ")}</span>,
      },
    ],
    [t],
  );

  if (unavailable) {
    return (
      <Alert>
        <AlertDescription>{t("claims.radar.unavailable")}</AlertDescription>
      </Alert>
    );
  }

  const secured = result ? result.excluded.notice + result.excluded.hemmung : 0;

  return (
    <div className="flex flex-col gap-4" data-guide-id="settings.defects.claimsRadar">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <p className="max-w-3xl text-sm">
          {result
            ? t("claims.radar.summary", { count: result.claims.length, days: result.days })
            : t("claims.radar.description")}
          {result && secured ? ` ${t("claims.radar.secured", { count: secured })}` : null}
        </p>
        <div className="flex flex-col gap-1">
          <Label>{t("claims.radar.horizon")}</Label>
          <Select value={String(days)} onValueChange={(value) => onDaysChange(Number(value))}>
            <SelectTrigger className="w-40" aria-label={t("claims.radar.horizon")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RADAR_HORIZONS.map((horizon) => (
                <SelectItem key={horizon} value={String(horizon)}>
                  {t("claims.radar.days", { count: horizon })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{t("defectsPanel.loadError", { error })}</AlertDescription>
        </Alert>
      ) : null}

      {result?.truncated ? (
        <Alert>
          <AlertDescription>{t("claims.radar.truncated")}</AlertDescription>
        </Alert>
      ) : null}

      <DataTable
        columns={columns}
        data={result?.claims ?? []}
        loading={loading}
        onRowClick={(claim) => onOpenDefect(claim.defect)}
        emptyMessage={
          <span className="inline-flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" aria-hidden />
            {t("claims.radar.empty", { days })}
          </span>
        }
      />

      {result ? <p className="text-xs text-muted-foreground">{result.noLegalAdvice}</p> : null}
    </div>
  );
}
